import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  ogImage,
  ogType = 'article',
  author,
  publishedTime,
  modifiedTime,
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const siteName = 'コインランドリー経営.com';
  const currentUrl = `${siteUrl}${window.location.pathname}`;
  const logoUrl = 'https://static.readdy.ai/image/f1769650ef219847a82938b2ccb51dd4/b20bf12da98b13bf6586d3e638cf3b35.jpeg';

  useEffect(() => {
    document.title = title;

    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setMetaTag('description', description);
    if (keywords) setMetaTag('keywords', keywords);
    setMetaTag('author', author || 'Laundrysite編集部');
    setMetaTag('robots', 'index,follow');

    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', currentUrl, true);
    setMetaTag('og:site_name', siteName, true);
    setMetaTag('og:locale', 'ja_JP', true);
    if (ogImage) {
      setMetaTag('og:image', ogImage, true);
      setMetaTag('og:image:alt', title, true);
      setMetaTag('og:image:width', '1200', true);
      setMetaTag('og:image:height', '630', true);
    }

    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    if (ogImage) setMetaTag('twitter:image', ogImage);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = currentUrl;

    const lastModified = modifiedTime || new Date().toISOString();
    setMetaTag('last-modified', lastModified);

    document.querySelectorAll('script[type="application/ld+json"][data-seo="true"]').forEach(el => el.remove());

    const isHomePage = window.location.pathname === '/';
    const publisherOrg = {
      '@type': 'Organization',
      name: siteName,
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
        width: 200,
        height: 60,
      },
    };

    const appendSchema = (schema: Record<string, unknown>) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'true');
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    };

    if (isHomePage) {
      // WebSite schema
      appendSchema({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        name: siteName,
        url: siteUrl,
        description: 'コインランドリー経営に関する総合情報サイト。開業・初期費用・運営・防犯対策まで解説。',
        inLanguage: 'ja-JP',
        publisher: publisherOrg,
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteUrl}/?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      });

      // WebPage schema
      appendSchema({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${currentUrl}#webpage`,
        url: currentUrl,
        name: title,
        description: description,
        inLanguage: 'ja-JP',
        isPartOf: { '@id': `${siteUrl}/#website` },
        datePublished: publishedTime || '2024-01-15T09:00:00+09:00',
        dateModified: lastModified,
        breadcrumb: { '@id': `${currentUrl}#breadcrumb` },
        publisher: publisherOrg,
        primaryImageOfPage: ogImage
          ? { '@type': 'ImageObject', url: ogImage, width: 1200, height: 630 }
          : undefined,
      });

      // BreadcrumbList schema
      appendSchema({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        '@id': `${currentUrl}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'ホーム',
            item: siteUrl,
          },
        ],
      });

      // FAQPage schema
      appendSchema({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': `${currentUrl}#faq`,
        mainEntity: [
          {
            '@type': 'Question',
            name: 'コインランドリー経営の初期費用はいくらかかりますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'コインランドリー経営の初期費用は、店舗規模や立地によって異なりますが、一般的に1,000万円〜3,000万円程度が目安です。機器代・内装工事費・保証金などが主な費用項目となります。',
            },
          },
          {
            '@type': 'Question',
            name: 'コインランドリー経営は儲かりますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'コインランドリー経営は無人運営が基本のため人件費を抑えられ、安定した収益が期待できます。立地・機器選定・集客施策が成功の鍵となります。',
            },
          },
          {
            '@type': 'Question',
            name: 'コインランドリーの開業に必要な資格・許可はありますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'コインランドリーの開業には特別な資格は不要ですが、店舗の建築確認申請や消防法に基づく届出が必要な場合があります。フランチャイズ加盟の場合は本部がサポートしてくれます。',
            },
          },
          {
            '@type': 'Question',
            name: 'コインランドリー経営の防犯対策はどうすればよいですか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'コインランドリーの防犯対策には、防犯カメラの設置・照明の充実・定期巡回・緊急連絡先の掲示などが効果的です。無人店舗だからこそ、しっかりとした防犯体制が重要です。',
            },
          },
          {
            '@type': 'Question',
            name: 'コインランドリーの売上はどのくらいですか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'コインランドリーの月間売上は立地や規模によって異なりますが、標準的な店舗で月50万円〜150万円程度が目安です。繁華街や住宅密集地では更に高い売上が見込めます。',
            },
          },
          {
            '@type': 'Question',
            name: 'コインランドリー開業の流れを教えてください。',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'コインランドリー開業の流れは、①事業計画の策定、②物件探し・契約、③機器メーカー・フランチャイズの選定、④内装工事・機器設置、⑤各種届出・許可申請、⑥スタッフ研修・試運転、⑦グランドオープンとなります。',
            },
          },
        ],
      });
    }

    // Article schema
    const articleSchema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${currentUrl}#article`,
      headline: title,
      description: description,
      author: {
        '@type': 'Organization',
        name: author || 'Laundrysite編集部',
        url: siteUrl,
      },
      publisher: publisherOrg,
      datePublished: publishedTime || '2024-01-15T09:00:00+09:00',
      dateModified: lastModified,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${currentUrl}#webpage`,
      },
      inLanguage: 'ja-JP',
      keywords: keywords || '',
      url: currentUrl,
      isPartOf: { '@id': `${siteUrl}/#website` },
      articleSection: 'コインランドリー経営',
      wordCount: 5000,
    };

    if (ogImage) {
      articleSchema.image = {
        '@type': 'ImageObject',
        url: ogImage,
        width: 1200,
        height: 630,
      };
    }

    appendSchema(articleSchema);

    // LocalBusiness schema (ホームページのみ)
    if (isHomePage) {
      appendSchema({
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': `${siteUrl}/#localbusiness`,
        name: siteName,
        url: siteUrl,
        description: 'コインランドリー経営に関する総合情報サイト。開業・初期費用・運営・防犯対策まで解説。',
        address: {
          '@type': 'PostalAddress',
          addressLocality: '大阪市中央区',
          addressRegion: '大阪府',
          addressCountry: 'JP',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 34.6722,
          longitude: 135.5028,
        },
        inLanguage: 'ja-JP',
        logo: {
          '@type': 'ImageObject',
          url: logoUrl,
        },
      });
    }

    return () => {
      document.querySelectorAll('script[type="application/ld+json"][data-seo="true"]').forEach(el => el.remove());
    };
  }, [title, description, keywords, ogImage, ogType, author, publishedTime, modifiedTime, currentUrl, siteUrl, siteName, logoUrl]);

  return null;
};
